// src/services/trackService.ts
import { API_BASE_URL } from "@/config";
import { getAuthHeaders } from "./authService";

// Define an extended Track type:
export interface TrackWithExtras {
  id: number;
  title: string;
  track_number?: number | null;
  duration?: number | null;
  file_path?: string;
  explicit?: boolean;
  preview_url?: string | null;
  album_id?: number;
  artist_id?: number;
  // Bundle endpoint may return track_id instead of id
  track_id?: number | string;
  // Bundle endpoint returns signed_url for authenticated playback
  signed_url?: string | null;
  // Access control fields from bundle endpoint
  access_type?: string;
  access_granted?: boolean;
  access_reason?: string;
  // Album info (plural from some endpoints)
  albums?: {
    title: string;
    artwork_url: string;
  };
  // Album info (singular from bundle endpoint)
  album?: {
    id: number | string;
    title?: string;
    artwork_url?: string;
  };
  artists?: {
    name: string;
    image_url: string;
  };
}


/**
 * Fetch tracks with artist and album information
 * @param limit Maximum number of tracks to fetch
 * @param offset Number of tracks to skip
 * @returns Array of tracks with artist and album information
 */
export const fetchTracks = async (
  limit: number = 20,
  offset: number = 0
): Promise<TrackWithExtras[]> => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/tracks?limit=${limit}&offset=${offset}`,
      {
        headers: getAuthHeaders(),
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to fetch tracks");
    }

    const data = await response.json();
    return data.tracks;
  } catch (error) {
    console.error("Error fetching tracks:", error);
    return [];
  }
};

/**
 * Fetch a single track by ID
 * @param trackId The ID of the track to fetch
 * @returns The track with artist and album information or null if not found
 */
export const fetchTrackById = async (
  trackId: number
): Promise<TrackWithExtras | null> => {
  try {
    const response = await fetch(`${API_BASE_URL}/tracks/${trackId}`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to fetch track");
    }

    const data = await response.json();
    return data.track;
  } catch (error) {
    console.error(`Error fetching track ${trackId}:`, error);
    return null;
  }
};

/**
 * Fetch tracks by artist ID
 * @param artistId The ID of the artist
 * @param limit Maximum number of tracks to fetch
 * @param offset Number of tracks to skip
 * @returns Array of tracks by the artist
 */
export const fetchTracksByArtist = async (
  artistId: number,
  limit: number = 20,
  offset: number = 0
): Promise<TrackWithExtras[]> => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/artist/${artistId}/tracks?limit=${limit}&offset=${offset}`,
      {
        headers: getAuthHeaders(),
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to fetch artist tracks");
    }

    const data = await response.json();
    return data.tracks;
  } catch (error) {
    console.error(`Error fetching tracks for artist ${artistId}:`, error);
    return [];
  }
};

/**
 * Fetch tracks by album ID
 * @param albumId The ID of the album
 * @returns Array of tracks in the album
 */
export const fetchTracksByAlbum = async (
  albumId: number
): Promise<TrackWithExtras[]> => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/albums/${albumId}/tracks`,
      {
        headers: getAuthHeaders(),
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to fetch album tracks");
    }

    const data = await response.json();
    return data.tracks;
  } catch (error) {
    console.error(`Error fetching tracks for album ${albumId}:`, error);
    return [];
  }
};

export async function searchTracks(query: string) {
  try {
    const response = await fetch(`${API_BASE_URL}/track/search?q=${encodeURIComponent(query)}`);
    if (!response.ok) {
      throw new Error('Failed to search tracks');
    }
    return await response.json();
  } catch (error) {
    console.error('Error searching tracks:', error);
    throw error;
  }
}

export async function fetchTrack(trackId: string) {
  try {
    const response = await fetch(`${API_BASE_URL}/track/${trackId}`);
    if (!response.ok) {
      throw new Error('Failed to fetch track details');
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching track:', error);
    throw error;
  }
}

export async function fetchTrackStreamUrl(trackId: string) {
  try {
    const response = await fetch(`${API_BASE_URL}/track/${trackId}/stream`);
    if (!response.ok) {
      throw new Error('Failed to fetch track stream URL');
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching track stream URL:', error);
    throw error;
  }
}

export async function getTracksByArtistId(artistId: string, limit: number = 100): Promise<TrackWithExtras[]> {
  try {
    if (!artistId) {
      console.warn('getTracksByArtistId called with invalid artistId');
      return [];
    }

    // Use authenticated bundle endpoint for artist top tracks
    const token = localStorage.getItem("access_token");
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE_URL}/bundle/artist/${artistId}/top-tracks?limit=${limit}`, {
      headers,
    });
    
    if (!response.ok) {
      // If unauthorized, return empty array (user might not be logged in)
      if (response.status === 401) {
        console.warn(`Unauthorized access to tracks for artist ${artistId}`);
        return [];
      }
      throw new Error('Failed to fetch tracks for artist');
    }
    
    const data = await response.json();
    // Bundle endpoint returns { bundle_type, artist_id, tracks }
    return data.tracks || [];
  } catch (error) {
    console.error(`Error in getTracksByArtistId for artist ${artistId}:`, error);
    return []; // Return empty array instead of throwing
  }
}

